import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useRoleName } from "@/lib/roles";
import {
  SquaresFour, Users, UserCircle, ClipboardText, EnvelopeSimple,
  Handshake, Trophy, ChartBar, FileText, ListChecks, Gear, SignOut,
  Buildings, Bell, Calendar, CurrencyInr, Target, Fire, CheckSquare, Wallet, CheckCircle,
  Briefcase, Books,
} from "@phosphor-icons/react";
import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { log } from "@/lib/logger";

const nav = [
  { to: "/", label: "Hotlist", icon: Fire, end: true },
  { to: "/dashboard", label: "Dashboard", icon: SquaresFour },
  { to: "/candidates", label: "Candidates", icon: UserCircle },
  { to: "/evaluations", label: "Evaluations", icon: Calendar, roles: ["admin", "team_lead", "sdl"] },
  { to: "/change-requests", label: "Change Requests", icon: CheckSquare, roles: ["admin", "team_lead", "sdl"] },
  { to: "/new-leads", label: "New Leads", icon: UserCircle, roles: ["admin", "team_lead", "hr", "sdl", "senior_executive"] },
  { to: "/job-workspace", label: "Job Workspace", icon: Briefcase, roles: ["admin", "team_lead", "senior_executive", "executive", "sde", "sdl"] },
  { to: "/jobs", label: "Job Library", icon: Books },
  { to: "/cold-emails", label: "Cold Emails", icon: EnvelopeSimple, roles: ["admin", "team_lead", "senior_executive", "executive", "sde", "sdl"] },
  { to: "/interviews", label: "Interviews", icon: Handshake },
  { to: "/analytics", label: "Analytics", icon: ChartBar, roles: ["admin", "team_lead"], section: "Insights" },
  { to: "/performance", label: "Performance", icon: ChartBar, roles: ["admin", "team_lead", "sdl", "senior_executive", "hr"], section: "Insights" },
  { to: "/targets", label: "Targets", icon: Target, roles: ["admin", "team_lead"], section: "Insights" },
  { to: "/incentives", label: "Incentives", icon: CurrencyInr, roles: ["admin"], section: "Insights" },
  { to: "/approvals", label: "Approvals", section: "Insights", icon: CheckSquare, roles: ["admin", "team_lead", "sdl", "hr", "executive", "senior_executive"] },
  { to: "/ledger", label: "Payout Ledger", icon: Wallet, roles: ["admin", "hr"], section: "Insights" },
  { to: "/assignments", label: "Assignments", icon: UserCircle, roles: ["admin", "team_lead", "hr", "sdl", "senior_executive"], section: "Insights" },
  { to: "/reports", label: "Reports", icon: FileText, roles: ["admin", "team_lead", "sdl", "senior_executive", "hr"], section: "Insights" },
  { to: "/employees", label: "Employee Management", icon: Users, roles: ["admin", "team_lead"], section: "Administration" },
  { to: "/masters", label: "Master Data", icon: Buildings, roles: ["admin"], section: "Administration" },
  { to: "/imports", label: "Data Import", icon: Buildings, roles: ["admin"], section: "Administration" },
  { to: "/system-health", label: "System Health", icon: Buildings, roles: ["admin"], section: "Administration" },
  { to: "/audit", label: "Audit Log", icon: ListChecks, roles: ["admin", "team_lead"], section: "Administration" },
  { to: "/settings", label: "Settings", icon: Gear, roles: ["admin"], section: "Administration" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const roleName = useRoleName();
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [openNotif, setOpenNotif] = useState(false);
  const [resubCount, setResubCount] = useState(0);

  // Phase 6C — poll persistent DB-backed notifications (replaces legacy /api/notifications).
  const refreshNotifs = useCallback(() => {
    api.get("/notifications/db?page_size=15")
      .then((r) => { setNotifs(r.data.rows || []); setUnreadCount(r.data.unread || 0); })
      .catch((err) => {
        if (err?.response?.status && err.response.status !== 401) {
          log.warn("Failed to load notifications:", err.message);
        }
      });
  }, []);

  useEffect(() => {
    if (!user) return;
    refreshNotifs();
    // Poll every 30 seconds
    const t = setInterval(refreshNotifs, 30000);
    if (user?.role === "executive" || user?.role === "team_lead" || user?.role === "sdl") {
      api.get("/incentives/records?status=Resubmission%20Required")
        .then((r) => setResubCount((r.data || []).length))
        .catch(() => setResubCount(0));
    }
    return () => clearInterval(t);
  }, [user, refreshNotifs]);

  const markRead = async (nid) => {
    try {
      await api.post(`/notifications/db/${nid}/read`);
      refreshNotifs();
    } catch { /* silent */ }
  };
  const markAllRead = async () => {
    try {
      await api.post("/notifications/db/mark-all-read");
      refreshNotifs();
    } catch { /* silent */ }
  };

  const items = nav
    .filter((n) => !n.roles || n.roles.includes(user?.role))
    .map((n) => {
      if (n.to === "/approvals") {
        // Phase 10 — Group Leads see this as their primary work queue.
        if (user?.role === "sdl") return { ...n, label: "Review & Approvals", badge: resubCount };
        if (user?.role === "executive" || user?.role === "sde") return { ...n, label: "My Resubmissions", badge: resubCount };
        if (user?.role === "team_lead" || user?.role === "senior_executive") return { ...n, badge: resubCount };
      }
      return n;
    });

  return (
    <div className="min-h-screen flex bg-slate-50">
      <aside className="w-64 shrink-0 bg-white border-r border-slate-200 flex flex-col" data-testid="sidebar">
        <div className="p-5 border-b border-slate-100 flex items-center gap-3">
          <div className="w-9 h-9 rounded-md bg-sky-600 grid place-items-center text-white">
            <Buildings size={18} weight="bold" />
          </div>
          <div>
            <div className="font-semibold tracking-tight text-slate-900">KGS SDMS</div>
            <div className="text-xs text-slate-500">Service Delivery</div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {items.map((n, idx) => {
            const prev = items[idx - 1];
            const showHeader = n.section && (!prev || prev.section !== n.section);
            return (
              <div key={n.to}>
                {showHeader && (
                  <div className="px-3 pt-4 pb-1 text-[10px] uppercase tracking-widest text-slate-400"
                       data-testid={`nav-section-${n.section.toLowerCase()}`}>
                    {n.section}
                  </div>
                )}
                <NavLink to={n.to} end={n.end}
                         data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                         className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}>
                  <n.icon size={18} weight="regular" />
                  <span>{n.label}</span>
                  {n.badge > 0 && (
                    <span className="ml-auto text-[10px] font-semibold bg-orange-500 text-white rounded-full px-1.5 min-w-[18px] text-center"
                          data-testid={`nav-badge-${n.label.toLowerCase().replace(/\s+/g, "-")}`}>
                      {n.badge}
                    </span>
                  )}
                </NavLink>
              </div>
            );
          })}
        </nav>
        <div className="p-3 border-t border-slate-100">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-8 h-8 rounded-full bg-slate-200 grid place-items-center text-slate-700 text-xs font-semibold">
              {(user?.name || "?").slice(0, 2).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-slate-800 truncate">{user?.name}</div>
              <div className="text-xs text-slate-500" data-testid="sidebar-user-role">{user?.role ? roleName(user.role) : ""}</div>
            </div>
            <button data-testid="logout-button"
              onClick={async () => { await logout(); navigate("/login"); }}
              className="text-slate-400 hover:text-red-600 transition-colors">
              <SignOut size={18} />
            </button>
          </div>
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-6 sticky top-0 z-10">
          <div>
            <h1 className="text-lg font-semibold text-slate-900">Service Delivery Console</h1>
            <p className="text-xs text-slate-500">Real-time visibility into candidates, activity and performance</p>
          </div>
          <div className="flex items-center gap-3 relative">
            <button className="relative p-2 rounded-md hover:bg-slate-100 transition-colors"
                    data-testid="notifications-toggle"
                    onClick={() => setOpenNotif((v) => !v)}>
              <Bell size={18} />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 text-[9px] font-semibold bg-red-500 text-white rounded-full grid place-items-center"
                      data-testid="notif-unread-badge">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </button>
            {openNotif && (
              <div className="absolute right-0 top-12 w-96 bg-white border border-slate-200 rounded-md z-20 shadow-lg overflow-hidden"
                   data-testid="notifications-panel">
                <div className="px-3 py-2 flex items-center justify-between border-b border-slate-100">
                  <div className="text-xs uppercase tracking-widest text-slate-500">
                    Notifications {unreadCount > 0 && <span className="ml-1 text-slate-700">· {unreadCount} unread</span>}
                  </div>
                  {unreadCount > 0 && (
                    <button onClick={markAllRead}
                            data-testid="notif-mark-all-read"
                            className="text-xs text-sky-600 hover:text-sky-800 inline-flex items-center gap-1">
                      <CheckCircle size={12} /> Mark all read
                    </button>
                  )}
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {notifs.length === 0 && <div className="text-sm text-slate-500 px-3 py-6 text-center">You're all caught up.</div>}
                  {notifs.map((n) => (
                    <div key={n.id}
                         data-testid={`notif-item-${n.id}`}
                         onClick={() => {
                           if (!n.read) markRead(n.id);
                           if (n.link) { setOpenNotif(false); navigate(n.link); }
                         }}
                         className={`px-3 py-2 border-t border-slate-100 cursor-pointer hover:bg-slate-50 ${!n.read ? "bg-sky-50/60" : ""}`}>
                      <div className="flex items-start gap-2">
                        {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-sky-500 mt-1.5 flex-shrink-0" />}
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-slate-800 truncate">{n.title}</div>
                          {n.message && <div className="text-xs text-slate-500 line-clamp-2">{n.message}</div>}
                          <div className="text-[10px] text-slate-400 mt-0.5">
                            {new Date(n.created_at).toLocaleString()}{n.kind && ` · ${n.kind.replace(/_/g, " ")}`}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </header>
        <main className="flex-1 p-6 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
